/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useState, useRef, useEffect } from 'react';
import { Box } from '@mui/material';

const EditableBox = ({
	content = '',
	fontsize,
	char,
	clr,
	txtAln,
	updateText,
	placeholder,
	hasGifTemplate
}) =>
{
	const [ isHovered, setIsHovered ] = useState(false);
	const divRef = useRef<HTMLDivElement>(null);
	const [ originalContent ] = useState(content);
	// using originalContent state because directly using content was causing cursor movement issue

	const moveCursorToEndOrStart = (newPos:null | number = null) =>
	{
		const selection = window.getSelection();

		if (!selection || !divRef.current) return;

		const range = document.createRange();
		let textLength = newPos ?? (divRef.current.textContent?.length || 0);

		if (textLength < 0 || textLength > (divRef?.current?.textContent?.length || 0))
		{
			textLength = divRef?.current?.textContent?.length || 0;
		}
		// Move to the end if content exists, otherwise to the start
		range.setStart(divRef.current.firstChild || divRef.current, textLength || 0);
		range.setEnd(divRef.current.firstChild || divRef.current, textLength || 0);

		selection.removeAllRanges();
		selection.addRange(range);
	};

	useEffect(() =>
	{
		const keyBoardCharRegex = /[ -~]+/;

		// need to update only when emoji is on the last index of the content
		if (divRef.current && content?.length < char && !content?.slice(-1).match(keyBoardCharRegex))
		{
			divRef.current.textContent = content || '';
		}
		else
		{
			updateText(content.slice(0, char));
		}
	}, [ content ]); // dependency required for updating emojis in occasions

	const getCurrentCursorPosition = () =>
	{
		const selection = window.getSelection();

		if (!selection?.anchorOffset || !selection?.focusOffset) return 0;

		return selection.anchorOffset;
	};

	const timeOut = useRef<any>(null);
	const handleInputChange = (e: React.FormEvent<HTMLDivElement>) =>
	{
		timeOut.current && clearTimeout(timeOut.current);
		const newText = (e.currentTarget.textContent || '');

		const currentPos = getCurrentCursorPosition();

		if (newText.length > char)
		{
			if (divRef?.current) divRef.current.textContent = newText.slice(0, char);
			timeOut.current = setTimeout(() => updateText(newText.slice(0, char)), 100);
			moveCursorToEndOrStart(currentPos - 1 === char ? char : currentPos);
		}
		else
		{
			timeOut.current = setTimeout(() => updateText(newText), content.length === 0 ? 0 : 100);
		}
	};

	const borderColor = hasGifTemplate ? clr || '#000000' : 'white';
	const borderStyle = isHovered ? `2px dashed ${borderColor}` : '2px solid transparent';

	return (
		<Box
			ref={divRef}
			contentEditable={isHovered}
			onInput={handleInputChange}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			sx={{
				width           : '100%',
				minHeight       : '30px',
				maxHeight       : '100px',
				padding         : '2px 4px',
				borderRadius    : '4px',
				color           : clr || '#000000',
				border          : borderStyle,
				outline         : 'none',
				overflowWrap    : 'break-word',
				wordWrap        : 'break-word',
				wordBreak       : 'break-word',
				whiteSpace      : 'pre-wrap',
				backgroundColor : 'transparent',
				transition      : 'border-color 0.3s ease',
				fontSize        : fontsize || '14px',
				textAlign       : txtAln || 'start',
				position        : 'relative',
				'&::after'      : !content
					? {
						content       : `'${placeholder || ''}'`,
						color         : hasGifTemplate ? '#9cc3e1' : '#d9d7d7',
						position      : 'absolute',
						top           : '2px',
						left          : '4px',
						pointerEvents : 'none'
					}
					: undefined
			}}
			suppressContentEditableWarning
		>
			{originalContent}
		</Box>
	);
};

export default EditableBox;
